import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import API_CONFIG from "../../config/apiConfig";

export default function ResetPasswordPopup({ officer, onClose }) {

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: officer?.name || "",
    email: officer?.email || "",
    userName: officer?.userName || "",
    newPassword: "",
    confirmPassword: "",
  });

  const [show, setShow] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setShow(true);
  }, []);

  const resetPasswordApi = async ({
    username,
    email,
    newPassword,
    confirmPassword,
  }) => {
    const token = localStorage.getItem("token");
    const response = await fetch(`${API_CONFIG.BASE_URL}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ username, email, newPassword, confirmPassword }),
    });

    const contentType = response.headers.get("content-type");
    if (!response.ok) {
      let errorMessage = "Failed to reset password";
      if (contentType?.includes("application/json")) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } else {
        errorMessage = await response.text();
      }
      throw new Error(errorMessage);
    }

    if (contentType?.includes("application/json")) {
      return response.json();
    } else {
      return await response.text();
    }
  };

  const redirectToAdmin = () => {
    navigate("/admin", { replace: true });
  };

  const handleChange = (e) => {
    setErrorMessage(""); // clear any old error
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      setErrorMessage("Passwords do not match!");
      return;
    }

    try {
      await resetPasswordApi({
        username: formData.userName,
        email: formData.email,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword,
      });

      setFormData({
        ...formData,
        newPassword: "",
        confirmPassword: "",
      });

      setShowSuccess(true);

      // Auto close after 2 seconds
      setTimeout(() => {
        setShow(false);
        setShowSuccess(false);
        if (typeof onClose === "function") onClose();
        redirectToAdmin();
      }, 2000);
    } catch (err) {
      setErrorMessage("Error resetting password: " + err.message);
    }
  };

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      if (typeof onClose === "function") onClose();
      redirectToAdmin();
    }, 300);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
      <div
        className={`w-full max-w-md p-6 bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl relative transform transition-all duration-300 ${
          show ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-2xl font-bold"
        >
          ❌
        </button>

        <h2 className="text-3xl font-bold text-center mb-6 text-blue-700">
          Reset Password
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            name="name"
            value={formData.name}
            readOnly
            className="w-full border p-3 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 font-medium"
          />

          <input
            type="email"
            name="email"
            value={formData.email}
            readOnly
            className="w-full border p-3 rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 font-medium"
          />

          <input
            type="password"
            name="newPassword"
            placeholder="New Password"
            value={formData.newPassword}
            onChange={handleChange}
            className="w-full border p-3 rounded-lg focus:ring-2 focus:ring-blue-400"
            required
          />

          <div>
            <input
              type="password"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full border p-3 rounded-lg focus:ring-2 ${
                errorMessage
                  ? "border-red-500 focus:ring-red-400"
                  : "focus:ring-blue-400"
              }`}
              required
            />
            {/* Inline Error Message */}
            {errorMessage && (
              <p className="text-red-500 text-sm mt-1 font-medium">
                {errorMessage}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white py-3 rounded-lg font-semibold shadow-md hover:from-blue-600 hover:to-blue-800 transition transform hover:scale-105"
          >
            🔒 Reset Password
          </button>
        </form>

        {/* ✅ Success Popup */}
        {showSuccess && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-2xl">
            <div className="text-center animate-fadeIn">
              <div className="text-green-600 text-4xl mb-3">✅</div>
              <p className="text-lg font-semibold text-gray-800">
                Password Reset Successfully!
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

ResetPasswordPopup.propTypes = {
  officer: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    userName: PropTypes.string,
  }),
  onClose: PropTypes.func,
};
